import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardStats } from '../hooks/useSuperAdminData';
import { useHistoricalStats, type ChangeIndicator as ChangeIndicatorType } from '../hooks/useHistoricalStats';

/**
 * Datos de una tarjeta KPI individual
 */
interface KPICardData {
  label: string;
  value: number;
  change?: ChangeIndicatorType | null;
}

/**
 * Props para KPICards
 */
interface KPICardsProps {
  stats: DashboardStats;
  loading: boolean;
}

/**
 * Tarjetas KPI para dashboard de SuperAdmin
 *
 * @version 2.0
 * @updated 2026-02-12
 * @module core/dashboard/components
 *
 * @description
 * Muestra 6 métricas clave con glassmorphism e indicadores de cambio temporal:
 * - Total Proyectos (vs 7 días)
 * - Proyectos Activos (vs 7 días)
 * - Total Clientes (vs 30 días)
 * - Clientes Activos (vs 30 días)
 * - Total Usuarios (vs 7 días)
 * - Usuarios Activos (vs 7 días)
 *
 * @remarks
 * **Características:**
 * - Grid responsive (3x2 → 2x3 → 1x6)
 * - Indicadores de cambio con colores (verde/rojo/gris)
 * - Loading states con skeleton shimmer
 * - useMemo para optimizar cálculo de cards
 * - Glassmorphism con WESTEER Design System
 *
 * @example
 * ```tsx
 * <KPICards stats={stats} loading={loading} />
 * ```
 */
export function KPICards({ stats, loading }: KPICardsProps): JSX.Element {
  const { t } = useTranslation('admin');
  const { getChange } = useHistoricalStats();

  /**
   * Preparar datos de las 6 cards con indicadores de cambio
   */
  const cards: KPICardData[] = useMemo(
    () => [
      {
        label: t('dashboard.kpi.totalProjects'),
        value: stats.totalProjects,
        change: getChange('totalProjects', '7d'),
      },
      {
        label: t('dashboard.kpi.activeProjects'),
        value: stats.activeProjects,
        change: getChange('activeProjects', '7d'),
      },
      {
        label: t('dashboard.kpi.totalTenants'),
        value: stats.totalTenants,
        change: getChange('totalTenants', '30d'),
      },
      {
        label: t('dashboard.kpi.activeTenants'),
        value: stats.activeTenants,
        change: getChange('activeTenants', '30d'),
      },
      {
        label: t('dashboard.kpi.totalUsers'),
        value: stats.totalUsers,
        change: getChange('totalUsers', '7d'),
      },
      {
        label: t('dashboard.kpi.activeUsers'),
        value: stats.activeUsersCount,
        change: getChange('activeUsersCount', '7d'),
      },
    ],
    [stats, t, getChange]
  );

  return (
    <div className="kpi-cards">
      {cards.map((card, index) => (
        <KPICard key={index} {...card} loading={loading} />
      ))}
    </div>
  );
}

/**
 * Tarjeta KPI individual
 *
 * @remarks
 * Muestra:
 * - Label en mayúsculas (kpi-label)
 * - Valor numérico grande (kpi-value)
 * - Indicador de cambio opcional (ChangeIndicator)
 * - Loading skeleton con shimmer animation
 *
 * @example
 * ```tsx
 * <KPICard
 *   label="Total Proyectos"
 *   value={48}
 *   change={{ value: 15.3, direction: 'up', period: '7d' }}
 *   loading={false}
 * />
 * ```
 */
function KPICard({
  label,
  value,
  change,
  loading,
}: KPICardData & { loading: boolean }): JSX.Element {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">
        {loading ? <div className="kpi-skeleton" /> : value}
      </div>
      {!loading && change && <ChangeIndicator {...change} />}
    </div>
  );
}

/**
 * Indicador visual de cambio temporal
 *
 * @remarks
 * Muestra:
 * - Porcentaje de cambio con signo (+/-/sin signo)
 * - Flecha direccional (↑ ↓ →)
 * - Período de comparación (vs 7d / vs 30d)
 * - Color según dirección:
 *   - Verde (up): var(--success)
 *   - Rojo (down): var(--error)
 *   - Gris (neutral): var(--text-tertiary)
 *
 * @example
 * ```tsx
 * <ChangeIndicator value={15.3} direction="up" period="7d" />
 * // Renderiza: "+15.3% ↑ vs 7d" en verde
 * ```
 */
function ChangeIndicator({
  value,
  direction,
  period,
}: ChangeIndicatorType): JSX.Element {
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
  const sign = direction === 'up' ? '+' : direction === 'down' ? '-' : '';
  const periodLabel = period === '7d' ? '7d' : '30d';

  return (
    <div
      className={`kpi-change kpi-change-${direction}`}
      aria-label={`${sign}${value.toFixed(1)}% vs ${periodLabel}`}
    >
      <span className="kpi-change-value">
        {sign}
        {value.toFixed(1)}%
      </span>
      <span className="kpi-change-arrow">{arrow}</span>
      <span className="kpi-change-period">vs {periodLabel}</span>
    </div>
  );
}
